// ============================================
// BusinessBrandingTab.jsx
// File: src/components/settings/BusinessBrandingTab.jsx
// ============================================
// Full branding section with logo upload and brand color picker

import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, 
  Trash2, 
  Image as ImageIcon, 
  Palette, 
  CheckCircle, 
  AlertCircle,
  Loader,
  Eye,
  X,
  Info
} from 'lucide-react';
import api from '../../services/api';

// Preset brand colors for quick selection
const PRESET_COLORS = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Gray', value: '#6B7280' },
];

const BusinessBrandingTab = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAlt, setUploadingAlt] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Branding data
  const [branding, setBranding] = useState({
    logoUrl: null,
    logoUrlAlt: null,
    brandColor: '#3B82F6',
    businessName: ''
  });
  
  // Color picker state
  const [customColor, setCustomColor] = useState('#3B82F6');
  const [showPreview, setShowPreview] = useState(false);
  
  // File input refs
  const logoInputRef = useRef(null);
  const logoAltInputRef = useRef(null);

  // Load branding on mount
  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/business/branding');
      setBranding(response.data);
      setCustomColor(response.data.brandColor || '#3B82F6');
    } catch (err) {
      console.error('Error loading branding:', err);
      setError('Failed to load branding settings.');
    } finally {
      setLoading(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (event, isAlt = false) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be under 2MB.');
      return;
    }

    try {
      isAlt ? setUploadingAlt(true) : setUploading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/business/logo?isAlt=${isAlt}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update state with new logo URL
      setBranding(prev => ({
        ...prev,
        [isAlt ? 'logoUrlAlt' : 'logoUrl']: response.data.logoUrl
      }));

      setSuccess(response.data.message || 'Logo uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.error || 'Failed to upload logo.');
    } finally {
      isAlt ? setUploadingAlt(false) : setUploading(false);
      // Reset file input
      if (isAlt) {
        logoAltInputRef.current.value = '';
      } else {
        logoInputRef.current.value = '';
      }
    }
  };

  // Handle logo delete
  const handleLogoDelete = async (isAlt = false) => {
    if (!confirm(`Are you sure you want to delete this ${isAlt ? 'alternate ' : ''}logo?`)) {
      return;
    }

    try {
      isAlt ? setUploadingAlt(true) : setUploading(true);
      setError(null);

      await api.delete(`/business/logo?isAlt=${isAlt}`);

      setBranding(prev => ({
        ...prev,
        [isAlt ? 'logoUrlAlt' : 'logoUrl']: null
      }));

      setSuccess(`${isAlt ? 'Alternate logo' : 'Logo'} deleted successfully.`);
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error deleting logo:', err);
      setError(err.response?.data?.error || 'Failed to delete logo.');
    } finally {
      isAlt ? setUploadingAlt(false) : setUploading(false);
    }
  };

  // Handle brand color save
  const handleSaveColor = async (color) => {
    try {
      setSaving(true);
      setError(null);

      await api.put('/business/branding', { brandColor: color });

      setBranding(prev => ({ ...prev, brandColor: color }));
      setCustomColor(color);
      
      setSuccess('Brand color updated!');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error saving brand color:', err);
      setError(err.response?.data?.error || 'Failed to save brand color.');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={18} />
          <span className="text-sm font-medium">{success}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Logo Upload Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Business Logo</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload your logo for emails, invoices, and public forms
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Primary Logo */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Primary Logo
              <span className="text-gray-400 font-normal ml-1">(light backgrounds)</span>
            </label>
            
            <div className="relative">
              {branding.logoUrl ? (
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-white">
                  <img 
                    src={branding.logoUrl} 
                    alt="Business Logo" 
                    className="max-h-24 max-w-full mx-auto object-contain"
                  />
                  <div className="flex justify-center gap-2 mt-3">
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploading}
                      className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => handleLogoDelete(false)}
                      disabled={uploading}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      {uploading ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-400 hover:bg-primary-50 transition-colors text-center"
                >
                  {uploading ? (
                    <Loader size={24} className="mx-auto text-gray-400 animate-spin" />
                  ) : (
                    <Upload size={24} className="mx-auto text-gray-400" />
                  )}
                  <p className="text-sm text-gray-600 mt-2">
                    {uploading ? 'Uploading...' : 'Click to upload logo'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    JPG, PNG, or WebP • Max 2MB
                  </p>
                </button>
              )}
              
              <input
                ref={logoInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={(e) => handleLogoUpload(e, false)}
                className="hidden"
              />
            </div>
          </div>

          {/* Alternate Logo (optional) */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Alternate Logo
              <span className="text-gray-400 font-normal ml-1">(dark backgrounds, optional)</span>
            </label>
            
            <div className="relative">
              {branding.logoUrlAlt ? (
                <div className="border-2 border-gray-700 rounded-lg p-4 bg-gray-800">
                  <img 
                    src={branding.logoUrlAlt} 
                    alt="Alternate Logo" 
                    className="max-h-24 max-w-full mx-auto object-contain"
                  />
                  <div className="flex justify-center gap-2 mt-3">
                    <button
                      onClick={() => logoAltInputRef.current?.click()}
                      disabled={uploadingAlt}
                      className="px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => handleLogoDelete(true)}
                      disabled={uploadingAlt}
                      className="px-3 py-1.5 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition-colors"
                    >
                      {uploadingAlt ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => logoAltInputRef.current?.click()}
                  disabled={uploadingAlt}
                  className="w-full border-2 border-dashed border-gray-600 rounded-lg p-6 bg-gray-800 hover:border-gray-500 transition-colors text-center"
                >
                  {uploadingAlt ? (
                    <Loader size={24} className="mx-auto text-gray-500 animate-spin" />
                  ) : (
                    <ImageIcon size={24} className="mx-auto text-gray-500" />
                  )}
                  <p className="text-sm text-gray-400 mt-2">
                    {uploadingAlt ? 'Uploading...' : 'Click to upload (optional)'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    White/light version for dark backgrounds
                  </p>
                </button>
              )}
              
              <input
                ref={logoAltInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={(e) => handleLogoUpload(e, true)}
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Logo tips */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Logo Tips</p>
              <ul className="mt-1 space-y-1 text-blue-600">
                <li>• Use a transparent PNG for best results</li>
                <li>• Recommended size: 400x100 pixels or similar aspect ratio</li>
                <li>• Keep file size under 500KB for faster loading</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Color Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Brand Color</h2>
            <p className="text-sm text-gray-600 mt-1">
              Used for buttons, accents, and customer-facing pages
            </p>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Eye size={16} />
            Preview
          </button>
        </div>

        {/* Preset Colors */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Select
          </label>
          <div className="flex flex-wrap gap-2">
            {PRESET_COLORS.map((color) => (
              <button
                key={color.value}
                onClick={() => handleSaveColor(color.value)}
                disabled={saving}
                className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                  branding.brandColor === color.value
                    ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
              >
                {branding.brandColor === color.value && (
                  <CheckCircle size={16} className="mx-auto text-white drop-shadow-md" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Color */}
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Color
            </label>
            <div className="flex gap-2">
              <div className="relative">
                <input
                  type="color"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                />
              </div>
              <input
                type="text"
                value={customColor.toUpperCase()}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                    setCustomColor(val);
                  }
                }}
                placeholder="#3B82F6"
                className="input w-32 font-mono text-sm"
              />
              <button
                onClick={() => handleSaveColor(customColor)}
                disabled={saving || customColor === branding.brandColor || !/^#[0-9A-Fa-f]{6}$/.test(customColor)}
                className="btn-primary px-4"
              >
                {saving ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  'Apply'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Current color indicator */}
        <div className="mt-4 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div 
            className="w-8 h-8 rounded-lg border border-gray-300"
            style={{ backgroundColor: branding.brandColor }}
          />
          <div>
            <p className="text-sm font-medium text-gray-900">Current Brand Color</p>
            <p className="text-xs text-gray-500 font-mono">{branding.brandColor?.toUpperCase()}</p>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {showPreview && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Preview</h2>
          <p className="text-sm text-gray-600 mb-4">
            See how your branding looks on customer-facing pages
          </p>
          
          {/* Mock quote form header */}
          <div 
            className="rounded-lg overflow-hidden border border-gray-200"
            style={{ background: `linear-gradient(135deg, ${branding.brandColor}, ${branding.brandColor}dd)` }}
          >
            <div className="p-6 text-center text-white">
              {branding.logoUrl ? (
                <img 
                  src={branding.logoUrl} 
                  alt="Logo Preview"
                  className="h-12 mx-auto mb-3 object-contain"
                />
              ) : (
                <div className="text-2xl font-bold mb-2">{branding.businessName || 'Your Business'}</div>
              )}
              <p className="text-white/90">Get Your Free Quote</p>
            </div>
          </div>

          {/* Mock button preview */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-3">Button Preview</p>
            <div className="flex flex-wrap gap-3">
              <button
                className="px-6 py-2 rounded-lg text-white font-semibold"
                style={{ backgroundColor: branding.brandColor }}
              >
                Get My Quote
              </button>
              <button
                className="px-6 py-2 rounded-lg font-semibold border-2"
                style={{ 
                  borderColor: branding.brandColor, 
                  color: branding.brandColor 
                }}
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Where branding is used */}
      <div className="card bg-gradient-to-br from-gray-50 to-gray-100">
        <h3 className="font-medium text-gray-900 mb-3">Where Your Branding Appears</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <CheckCircle size={16} className="text-green-500" />
            <span>Public Quote Form</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <CheckCircle size={16} className="text-green-500" />
            <span>Email Templates</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <CheckCircle size={16} className="text-green-500" />
            <span>PDF Invoices</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <CheckCircle size={16} className="text-green-500" />
            <span>Service Agreements</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <CheckCircle size={16} className="text-green-500" />
            <span>Customer Portal</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <CheckCircle size={16} className="text-green-500" />
            <span>QR Code Pages</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessBrandingTab;